import { FormEvent, useState } from 'react';
import {
    Button,
    PasswordInput,
    TextInput,
} from '@mantine/core';
import { Link } from '@carbon/react';
import {
    ArrowRight,
    Email,
    Locked,
} from '@carbon/icons-react';
import { useNavigate } from 'react-router-dom';
import { defineMessages, useIntl } from 'react-intl';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';

import styles from './LoginForm.module.scss';

import { useAuth } from 'router/authentication/AuthProvider';
import { useLoginMutation, useResendActivationEmailMutation } from 'api/accountEndpoints';

const ACCOUNT_INACTIVE_CODE = 'ACCOUNT_INACTIVE';

const getApiErrorCode = (error: unknown) => {
    if (!error || typeof error !== 'object' || !('data' in error)) {
        return undefined;
    }

    const { data } = error as FetchBaseQueryError;
    if (!data || typeof data !== 'object' || !('code' in data)) {
        return undefined;
    }

    const { code } = data as { code?: unknown };
    return typeof code === 'string' ? code : undefined;
};

const LoginForm = () => {
    const [login, { isLoading }] = useLoginMutation();
    const [resendActivationEmail, { isLoading: isResendingActivation }] = useResendActivationEmailMutation();
    const { login: saveTokenToContext } = useAuth();
    const navigate = useNavigate();
    const intl = useIntl();
    const [errorMessage, setErrorMessage] = useState('');
    const [inactiveAccountEmail, setInactiveAccountEmail] = useState('');

    const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        const { currentTarget } = e;
        const formData = new FormData(currentTarget);
        const email = formData.get('email');
        const password = formData.get('password');
        if (email === null || password === null) {
            return;
        }

        const normalizedEmail = String(email).trim();
        setErrorMessage('');
        setInactiveAccountEmail('');

        try {
            const response = await login({
                email: normalizedEmail,
                password: password as string,
            }).unwrap();

            saveTokenToContext(response.token);
            navigate('/events');
        } catch (error) {
            if (getApiErrorCode(error) === ACCOUNT_INACTIVE_CODE) {
                setInactiveAccountEmail(normalizedEmail);
                setErrorMessage(intl.formatMessage(messages.inactiveAccount));
                return;
            }

            setErrorMessage(intl.formatMessage(messages.invalidCredentials));
        }
    };

    const handleResendActivation = async () => {
        if (!inactiveAccountEmail) {
            return;
        }

        await resendActivationEmail({ email: inactiveAccountEmail }).unwrap().catch(() => undefined);
    };

    return (
        <form className={styles.container} onSubmit={handleSubmit}>
            <TextInput
                id="email"
                name="email"
                classNames={{
                    root: styles.fieldRoot,
                    label: styles.fieldLabel,
                    input: styles.fieldInput,
                }}
                label={intl.formatMessage(messages.emailLabel)}
                placeholder={intl.formatMessage(messages.emailPlaceholder)}
                leftSection={<Email size={16} className={styles.fieldIcon} />}
                leftSectionPointerEvents="none"
                type="email"
                size="md"
                required
            />
            <PasswordInput
                id="password"
                name="password"
                classNames={{
                    root: styles.fieldRoot,
                    label: styles.fieldLabel,
                    input: styles.fieldInput,
                    innerInput: styles.passwordInnerInput,
                    section: styles.passwordSection,
                }}
                label={intl.formatMessage(messages.passwordLabel)}
                placeholder={intl.formatMessage(messages.passwordPlaceholder)}
                leftSection={<Locked size={16} className={styles.fieldIcon} />}
                leftSectionPointerEvents="none"
                size="md"
                required
            />
            <div className={styles.actionsRow}>
                <Link
                    size="sm"
                    className={styles.recoveryLink}
                    onClick={() => { navigate('/recovery'); }}
                >
                    {intl.formatMessage(messages.recoverPassword)}
                </Link>
            </div>
            <Button
                className={styles.submitButton}
                type="submit"
                rightSection={<ArrowRight size={16} />}
                loading={isLoading}
                loaderProps={{ type: 'dots' }}
            >
                {intl.formatMessage(messages.submitButton)}
            </Button>
            {errorMessage && (
                <div className={styles.loginError} role="alert">
                    <p>{errorMessage}</p>
                    {inactiveAccountEmail && (
                        <Button
                            variant="subtle"
                            size="compact-sm"
                            className={styles.resendActivationButton}
                            loading={isResendingActivation}
                            onClick={handleResendActivation}
                        >
                            {intl.formatMessage(messages.resendActivationEmail)}
                        </Button>
                    )}
                </div>
            )}
            <p className={styles.registerText}>
                {intl.formatMessage(messages.noAccount)}
                {' '}
                <Link
                    size="sm"
                    className={styles.registerLink}
                    onClick={() => { navigate('/register'); }}
                >
                    {intl.formatMessage(messages.register)}
                </Link>
            </p>
        </form>

    );
};

const messages = defineMessages({
    emailLabel: {
        id: 'src.Screens.Login.LoginForm.LoginForm.emailLabel',
        defaultMessage: 'Email',
    },
    passwordLabel: {
        id: 'src.Screens.Login.LoginForm.LoginForm.passwordLabel',
        defaultMessage: 'Hasło',
    },
    emailPlaceholder: {
        id: 'src.Screens.Login.LoginForm.LoginForm.emailPlaceholder',
        defaultMessage: 'Wprowadź adres email',
    },
    passwordPlaceholder: {
        id: 'src.Screens.Login.LoginForm.LoginForm.passwordPlaceholder',
        defaultMessage: 'Wprowadź hasło',
    },
    emailWarning: {
        id: 'src.Screens.Login.LoginForm.LoginForm.emailWarning',
        defaultMessage: 'Wprowadzony mail może być nieprawidłowy.',
    },
    passwordWarning: {
        id: 'src.Screens.Login.LoginForm.LoginForm.passwordWarning',
        defaultMessage: 'Wprowadzone hasło może być nieprawidłowe.',
    },
    invalidCredentials: {
        id: 'src.Screens.Login.LoginForm.LoginForm.invalidCredentials',
        defaultMessage: 'Nieprawidłowy adres e-mail lub hasło',
    },
    inactiveAccount: {
        id: 'src.Screens.Login.LoginForm.LoginForm.inactiveAccount',
        defaultMessage: 'Konto nie zostało jeszcze aktywowane. Sprawdź skrzynkę e-mail i kliknij link aktywacyjny albo wyślij wiadomość aktywacyjną ponownie.',
    },
    resendActivationEmail: {
        id: 'src.Screens.Login.LoginForm.LoginForm.resendActivationEmail',
        defaultMessage: 'Wyślij wiadomość ponownie',
    },
    submitButton: {
        id: 'src.Screens.Login.LoginForm.LoginForm.submitButton',
        defaultMessage: 'Zaloguj się',
    },
    recoverPassword: {
        id: 'src.Screens.Login.LoginForm.LoginForm.recoverPassword',
        defaultMessage: 'Zapomniałeś hasła?',
    },
    noAccount: {
        id: 'src.Screens.Login.LoginForm.LoginForm.noAccount',
        defaultMessage: 'Nie masz konta?',
    },
    register: {
        id: 'src.Screens.Login.LoginForm.LoginForm.register',
        defaultMessage: 'Zarejestruj się',
    },
});

export default LoginForm;
